import { Typography } from '@mui/material';
import { useSearchParams } from 'react-router';
import useSWR from 'swr';

import { fetchPropertyDetails } from '~/components/portfolio/detailPage/fetchPropertyDetails';
import { PropertyDetailPage } from '~/components/portfolio/detailPage/PropertyDetailPage';
import { PropertyDetailPageHeader } from '~/components/portfolio/detailPage/PropertyDetailPageHeader';
import { RowPreviewPanel } from '~/components/rowPreview/RowPreviewPanel';
import type { RowPreview } from '~/components/rowPreview/useRowPreview';
import { dateToDateString, getStoredOrDefaultMonthRange } from '~/components/utils/dateUtils';

// The page's own period when the URL carries one, as the property page reads it.
function usePreviewPeriod(): [string, string] {
  const [searchParams] = useSearchParams();
  const start = searchParams.get('start_date');
  const end = searchParams.get('end_date');
  if (start && end) return [start, end];
  const fallback = getStoredOrDefaultMonthRange();
  return [dateToDateString(fallback.start), dateToDateString(fallback.end)];
}

function PropertyPreviewContent({ propertyId }: { propertyId: string }) {
  const [start, end] = usePreviewPeriod();
  const { data, error } = useSWR(
    ['property-preview', propertyId, start, end] as const,
    ([, id, startDate, endDate]) => fetchPropertyDetails(id, startDate, endDate),
  );

  if (error && !data) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
        This property couldn’t be loaded.
      </Typography>
    );
  }
  if (!data) {
    return (
      <PropertyDetailPageHeader
        property={null}
        onBack={null}
        backLabel=""
        elevatorCount={undefined}
        compact
      />
    );
  }
  return (
    <PropertyDetailPage
      key={propertyId}
      property={data}
      onBack={null}
      backLabel=""
      variant="preview"
    />
  );
}

/** `getPropertyId` maps a row to its property, for tables keyed on something else. */
export function PropertyPreviewPanel({
  rowPreview,
  getPropertyId = (rowId) => rowId,
  onOpenFullPage,
}: {
  rowPreview: RowPreview;
  getPropertyId?: (rowId: string) => string | null;
  onOpenFullPage: (propertyId: string) => void;
}) {
  return (
    <RowPreviewPanel
      rowPreview={rowPreview}
      ariaLabel="Property preview"
      onOpenFullPage={(rowId) => {
        const propertyId = getPropertyId(rowId);
        if (propertyId) onOpenFullPage(propertyId);
      }}
    >
      {(rowId) => {
        const propertyId = getPropertyId(rowId);
        return propertyId ? <PropertyPreviewContent propertyId={propertyId} /> : null;
      }}
    </RowPreviewPanel>
  );
}
